import * as React from "react";
import { PropsType } from "../../containers/ResultsContainer";
import { Item } from '../../types/Item';
import './Results.scss'
import { Button, Spinner } from "reactstrap";
import fallbackImg from "../../images/fallback_thumb.jpg";
import editImg from "../../images/Edit-Icon.svg";
import orderImg from "../../images/Order-Icon.svg";
import { Store } from "dw_azsearchstore";
import { useTranslation, Translation } from 'react-i18next';
import { easterEgg } from '../easterEgg';
import { trackEvent } from "./../../telemetry/TelemetryService";
import PopupContext from "../../contexts/popupContext";
import MyMedia from "../my-media/my-media";
import { useConfig } from "../../contexts/configContext";

interface Props {
    isFetching: boolean;
    item: Item;
    addFilter?: (facetKey: string, value: string) => void;
    addSorter?: (sorterKey: string, sorterDirection: string, value: string) => void;
    facets: { [key: string]: Store.Facet }
    isOpen: boolean;
}

const ItemCardCaption: React.StatelessComponent<Props> = (props) => {
    function getLanguageFullName(lang: string) {
        switch (lang) {
            case "en": lang = "English"; break;
            case "es": lang = "Spanish"; break;
            case "fr": lang = "French"; break;
            case "de": lang = "German"; break;
            case "pt": lang = "Portuguese"; break;
            case "nl": lang = "Dutch"; break;
            case "pl": lang = "Polish"; break;
            case "it": lang = "Italian"; break;
            case "ja": lang = "Japanese"; break;
            case "sv": lang = "Swedish"; break;
        }
        return lang;
    }

    /** Gets the subtitle items (e.g.: document type, subtitle and language flag) with a separator between them (the "|" character) */
    function getSubititleItems() {
        let subtitleItems = new Array();
        if (props.item.DocumentType) {
            subtitleItems.push(
                <span className="item-card-subtitle-docType subtitle-item">
                    <span>
                        <Translation>{t => t(props.item.DocumentType)}</Translation>
                    </span>
                </span>
            );
        }

        if (props.item.Subtitle) {
            subtitleItems.push(
                <span className="item-card-subtitle-text subtitle-item">{props.item.Subtitle}</span>
            );
        }

        if (props.item.LanguageCode) {
            subtitleItems.push(
                <span className="item-card-subtitle-flag subtitle-item">
                    <Translation>{t => t(getLanguageFullName(props.item.LanguageCode))}</Translation>
                </span>
            );
        }

        let separator = <span className="item-card-subtitle-separator">|</span>;
        let itemCardSubtitleContent = subtitleItems.reduce((previous, current) => [previous, separator, current]);
        return itemCardSubtitleContent;
    }

    return (
        <div className="item-card-header">
            <div className="item-card-title" >
                <a className="item-card-title-link" href={props.item.OpenLink} target="_blank">{props.item.Title}</a>
            </div>
            {
                false ?
                    <div className="item-card-header-edit">
                        <a target="_blank" href={props.item.PersonalizationLink}>
                            <img src={editImg} />
                            <span><Translation>{t => t('Edit')}</Translation></span>
                        </a>
                    </div>
                    : null
            }
            <div className="item-card-subtitle">
                {getSubititleItems()}
            </div>
        </div>
    );
}

const ItemCardContent: React.StatelessComponent<Props> = (props) => {
    const { t, i18n } = useTranslation();
    const context = React.useContext(PopupContext)
    const { myMedia } = useConfig();

    let { PersonalizationLink } = props.item;
    if (PersonalizationLink)
        PersonalizationLink = PersonalizationLink.replace('https://', '').replace('www.docuware.com/', 'https://www.docuware.com/user/login?destination=');

    /**
     * If the specified URL doesn't start with HTTP(S), then add HTTP to its start.
     * @param url URL to be verified
     * @returns Processed version of the URL (with HTTP)
     */
    function setUrlToHttp(url: string) {
        return (!url.match(/^https?:\/\//i))
            ? `http://${url}`
            : url;
    }

    //some links don't start with http://. in these cases it needs to be added manually
    function openLink(url: string) {
        url = setUrlToHttp(url);
        return window.open(url, "_blank");
    }

    function addFilter(facetKey: string, value: string) {
        if (props.addFilter)
            return props.addFilter(facetKey, value);
    }

    function isSelectedFacet(facetKey: string, value: string): boolean {
        let isSelected = false;
        let facet = props.facets[facetKey];
        isSelected = facet === undefined ? false : facet.filterClause.indexOf(value) > 1
        return isSelected;
    }

    function onOpenBtnClick() {
        trackEvent({
            name: 'ClickedOpenButton',
            properties: props.item
        })
    }

    let openButtonEl = props.item.OpenLink
        ? (
            <a className="btnOpen" href={setUrlToHttp(props.item.OpenLink)} target="_blank">
                <Button onClick={onOpenBtnClick}>{t('Open')}</Button>
            </a>
        )
        : ""

    function onPersonalizeBtnClick() {
        trackEvent({
            name: 'ClickedPersonalizeButton',
            properties: props.item
        });

        const popupContent = <MyMedia documentType={props.item.DocumentType} documentUri={props.item.PDFLocation} selectedLanguage={i18n.language} onErrorCallback={() => {return null}} onLoadCallback={ () => {return null} } />;
        context.setPopupContent(popupContent)
        context.setIsPopupVisible(true);
    }

    let personalizeReactFragment = props.item.PDFLocation
        ? <React.Fragment>
            <span><Translation>{t => t('or')}</Translation></span>
                <a className="btnPersonalize">
                    <Button className="btn-gold" onClick={onPersonalizeBtnClick}>{t('Personalize')}</Button>
                </a>
        </React.Fragment>
        : ""

    return (
        <div className="item-card-content-container">
            <div className="item-card-content-image-container">
                <img src={props.item.ThumbnailUri} onError={(e: any) => { e.target.src = fallbackImg }} onClick={() => openLink(props.item.OpenLink || "")} />
            </div>
            <div className="item-card-content-description">
                <span dangerouslySetInnerHTML={{ __html: props.item.Description }}></span>
                <div className="item-card-content-buttons">
                    {openButtonEl}
                    {personalizeReactFragment}
                </div>
                {
                    props.item.OrderLinkEMEA || props.item.OrderLinkUS
                        ? <div>
                            <hr></hr>
                            <div className="item-card-content-order">
                                <span><Translation>{t => t('Order')}</Translation>:</span>
                                {props.item.OrderLinkEMEA
                                    ? <span>
                                        <a href="https://www.docuware.com/eshop" target="_blank">
                                            <img src={orderImg}></img>
                                            <span>EMEA</span>
                                        </a>
                                    </span>
                                    : null}
                                {props.item.OrderLinkUS
                                    ? <span>
                                        <a href="https://www.docuware.com/main.asp?sig=dlx&lan=en&loc=us&dwdbkat=sal*&dwdblan=english&dwdbname=docuware+marketing+material+order+form*" target="_blank">
                                            <img src={orderImg}></img>
                                            <span>Americas</span>
                                        </a>
                                    </span>
                                    : null}
                            </div>
                        </div>
                        : null
                }
            </div>
            <div className="item-card-content-properties">
                {props.item.SalesSteps.length > 0
                    ? <div className="properties-block">
                        <span className="item-card-content-properties-description">{t('Sales Steps')}:</span>
                        <span className="item-card-content-properties-values">
                            {props.item.SalesSteps.map((item, index) =>
                                <span key={index}>
                                    <a className={isSelectedFacet("SalesSteps", item) ? "selected" : ""} onClick={() => { addFilter("SalesSteps", item) }}>
                                        {t(item)}
                                    </a>{props.item.SalesSteps[index + 1] ? ", " : ""}
                                </span>
                            )}
                        </span>
                    </div>
                    : ""}
                {props.item.Topic
                    ? <div className="properties-block">
                        <span className="item-card-content-properties-description">{t('Topic')}:</span>
                        <span className="item-card-content-properties-values">
                            {props.item.Topic.map((item, index) =>
                                <span key={index}>
                                    <a className={isSelectedFacet("Topic", item) ? "selected" : ""} onClick={() => { addFilter("Topic", item) }}>
                                        {t(item)}
                                    </a>{props.item.Topic[index + 1] ? ", " : ""}
                                </span>
                            )}
                        </span>
                    </div>
                    : ""}
                {props.item.Industries.length > 0
                    ? <div className="properties-block">
                        <span className="item-card-content-properties-description">{t('Industries')}:</span>
                        <span className="item-card-content-properties-values">
                            {props.item.Industries.map((item, index) =>
                                <span key={index}>
                                    <a className={isSelectedFacet("Industries", item) ? "selected" : ""} onClick={() => { addFilter("Industries", item) }}>
                                        {t(item)}
                                    </a>{props.item.Industries[index + 1] ? ", " : ""}
                                </span>
                            )}
                        </span>
                    </div>
                    : ""}
                {props.item.Module.length > 0
                    ? <div className="properties-block">
                        <span className="item-card-content-properties-description">{t('Module')}:</span>
                        <span>
                            {props.item.Module.map((item, index) =>
                                <span key={index}>
                                    {t(item)}
                                    {props.item.Module[index + 1] ? ", " : ""}
                                </span>
                            )}
                        </span>
                    </div>
                    : ""}
            </div>
        </div>
    );
}

export type State = {
    isOpen: boolean;
    isMobile: boolean;
};

class Results extends React.Component<PropsType, State> {

    constructor(props: PropsType) {
        super(props);

        let isMobile = this.isMobile();
        this.state = {
            isOpen: !isMobile,
            isMobile: isMobile
        };

        this.updateState = this.updateState.bind(this);
        this.toggleIsOpen = this.toggleIsOpen.bind(this);
        window.addEventListener('resize', this.updateState);
    }

    isMobile() {
        return window.innerWidth <= 768;
    }

    updateState() {
        let isMobile = this.isMobile();
        this.setState({
            isOpen: !isMobile,
            isMobile: isMobile
        });
    }

    toggleIsOpen() {
        this.setState({
            isOpen: !this.state.isOpen
        });
    }

    componentDidUpdate() {
        this.trackSearchResult();
    }

    /**
     * Gets the current facets filter clause
     * Result example: (SalesSteps/any(t: t eq 'Client Presentation')) and (Language eq 'English')
     */
    getCurrentFacetsFilterClause() {
        const facetFilters = [];
        const facetsDictionary = this.props.facets;
        for (let facetKey in facetsDictionary) {
            let facet = facetsDictionary[facetKey];
            let filterClause = facet.filterClause;
            if (filterClause != "" && filterClause != null)
                facetFilters.push(filterClause);
        }

        return facetFilters.join(" and ");
    }

    startedFetching = false
    trackSearchResult() {
        if (this.props.isFetching && !this.startedFetching)
            this.startedFetching = true
        else if (!this.props.isFetching && this.startedFetching) {
            this.startedFetching = false;

            const facets = this.getCurrentFacetsFilterClause();
            trackEvent({
                name: 'SearchExecuted',
                properties: {
                    facets: facets,
                    searchInput: this.props.searchInput,
                    parameters: this.props.searchParameters,
                    resultsCount: this.props.total
                }
            });
        }
    }

    /**
     * Renders the results into a React fragment
     * @param results 
     * @param addFilter 
     * @param facets 
     */
    private getRenderedResults(results: {}[], addFilter: (facetKey: string, value: string) => void, facets: { [key: string]: Store.Facet; }) {
        if (results) {
            return results.map((result: any, index: number) => {
                let propsItem = {
                    item: result,
                    addFilter: addFilter,
                    facets: facets
                } as Props;

                let isNew = false;
                const today = new Date();
                let displayAsNewUntilDate = new Date();
                if (propsItem.item.DisplayAsNewUntil != null) {
                    displayAsNewUntilDate = new Date(propsItem.item.DisplayAsNewUntil);
                }
                isNew = displayAsNewUntilDate > today ? true : false;

                return (
                    <div className="item-card" key={index}>
                        <div className="item-card-container">
                            {isNew ? <div className="item-card-newbadge"><Translation>{t => <p className="item-card-newbadge-text">{t('NEW')}</p>}</Translation></div> : <div></div>}
                            <ItemCardCaption {...propsItem}></ItemCardCaption></div>
                        <hr />
                        <ItemCardContent {...propsItem}></ItemCardContent>
                    </div>
                );
            })
        }

        return "";
    }

    render() {
        const { results, addFilter, facets, loadMore, total, isFetching } = this.props;

        // easterEgg
        if (results.length === 0) {
            easterEgg.enable();
        } else {
            easterEgg.disable();
        }

        const renderedResults = this.getRenderedResults(results, addFilter, facets);


        const ShowMore = () => {
            return (
                results && results.length > 0 && total > results.length ?
                    <div className="item-card">
                        <div className="show-more">
                            <a onClick={() => { loadMore() }}>
                                <Translation>{t => t('Show more')}</Translation>
                            </a>
                        </div>
                    </div> : ""
            )
        }

        const noResults = () => {
            return (
                <h2><Translation>{t => t('There are no results for your search')}</Translation>.</h2>
            )
        }


        return (
            <div id="results">
                {
                    isFetching ?
                        <div className="spinner-box">
                            <Spinner type="grow" color="secondary" style={{ width: '15rem', height: '15rem' }} />
                        </div> :
                        (results && results.length !== 0) ?
                            <React.Fragment>
                                {renderedResults}
                                <React.Fragment>
                                    {ShowMore()}
                                </React.Fragment>
                            </React.Fragment> : <div className="item-card">{noResults()}</div>

                }
            </div>
        );
    }
}

Results.contextType = PopupContext;
export default Results;


